<script setup>
import { ref, computed } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useVideoTranscription } from 'dashboard/composables/useVideoTranscription';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES, MESSAGE_TYPES } from '../constants';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const context = useMessageContext();
const { filteredCurrentChatAttachments, attachments, messageType } = context;
const {
  eligible: canTranscribe,
  state: transcription,
  pending: transcriptionPending,
  complete: transcriptionComplete,
  expanded: transcriptionExpanded,
  notice: transcriptionNotice,
  heading: transcriptionHeading,
  translation: transcriptionTranslation,
  toggle: toggleTranscription,
} = useVideoTranscription(context);

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});
</script>

<template>
  <!-- The AI control sits beside the bubble's bottom edge, as for text. -->
  <div
    class="flex w-fit min-w-0 max-w-full items-end gap-2"
    :class="{ 'flex-row-reverse': messageType === MESSAGE_TYPES.OUTGOING }"
  >
    <BaseBubble
      class="overflow-hidden p-3"
      data-bubble-name="video"
      @click="showGallery = true"
    >
      <div class="relative group rounded-lg overflow-hidden">
        <div
          v-if="isReel"
          class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
        >
          <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
        </div>
        <video
          v-if="!hasError"
          controls
          preload="metadata"
          class="rounded-lg skip-context-menu"
          :src="attachment.dataUrl"
          :class="{
            'max-w-48': isReel,
            'max-w-full': !isReel,
          }"
          @click.stop
          @error="handleError"
        />
        <div v-else class="p-3 text-sm" role="status" @click.stop>
          <p>此视频暂时无法在浏览器中播放。</p>
          <a :href="attachment.dataUrl" target="_blank" rel="noopener noreferrer" class="underline">打开或下载原视频</a>
        </div>
      </div>
      <div
        v-if="transcriptionExpanded && (transcriptionHeading || transcriptionNotice)"
        class="mt-3 flex flex-col gap-2 text-sm"
        data-video-transcript
        :aria-busy="transcriptionPending"
        @click.stop
      >
        <p v-if="transcriptionHeading" class="m-0 text-xs text-n-slate-11">
          {{ transcriptionHeading }}
        </p>
        <p
          v-if="transcription.transcript"
          class="m-0 whitespace-pre-wrap break-words"
        >
          {{ transcription.transcript }}
        </p>
        <p
          v-if="transcriptionTranslation"
          class="m-0 pt-2 border-t-2 border-n-slate-8 whitespace-pre-wrap break-words"
        >
          {{ transcriptionTranslation }}
        </p>
        <p
          v-if="transcriptionNotice"
          role="status"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ transcriptionNotice }}
        </p>
      </div>
    </BaseBubble>
    <button
      v-if="canTranscribe"
      type="button"
      class="borderchat-translation-toggle inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap !rounded-full !border !border-solid !border-n-blue-6 !bg-n-solid-1 px-3 py-1.5 text-sm leading-5 !text-n-blue-11 hover:!bg-n-blue-2 disabled:cursor-wait disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
      :aria-expanded="transcriptionComplete ? transcriptionExpanded : undefined"
      :aria-busy="transcriptionPending"
      :aria-label="
        transcriptionComplete && transcriptionExpanded
          ? '收起语音识别'
          : '识别视频语音并翻译为中文'
      "
      :title="
        transcriptionComplete && transcriptionExpanded
          ? '收起语音识别'
          : '识别视频语音并翻译为中文'
      "
      data-video-transcription-request
      @click.stop="toggleTranscription"
    >
      <span
        :class="
          transcriptionPending
            ? 'i-lucide-loader-circle animate-spin'
            : 'i-lucide-languages'
        "
        class="size-4"
        aria-hidden="true"
      />
      <span>识别视频内容</span>
    </button>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
